import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ZeplinTitle,
  ZeplinSubtitle,
  ZeplinStepIndicator,
  ZeplinFieldGroup,
  ZeplinLabel,
  ZeplinInput,
  ZeplinButton,
  ZeplinError,
  ZeplinValidCheck,
  ZeplinCepChip
} from '../../styles/ZeplinStyles';
import { LoadingSpinner, CheckboxContainer } from '../../styles/FormStyles';
import axios from 'axios';

// Ícone de check (validação positiva / stepper concluído / chip)
const CheckIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Ícone de alerta para erros inline
const AlertIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

interface EnderecoStepProps {
  onBack: () => void;
  onSubmit: () => void;
  isLoading: boolean;
}

const EnderecoStep: React.FC<EnderecoStepProps> = ({
  onBack,
  onSubmit,
  isLoading
}) => {
  const { register, watch, setValue, formState: { errors } } = useFormContext();
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [cepFound, setCepFound] = useState(false);
  const [cepFlash, setCepFlash] = useState(false);

  // Observar os valores dos campos para validação
  const watchedValues = watch();
  const addressData = watchedValues.address || {};

  // Núcleo da busca de CEP (lógica ViaCEP preservada)
  const fetchCep = async (rawCep: string) => {
    const cep = rawCep.replace(/\D/g, '');

    if (cep.length === 8) {
      setIsLoadingCep(true);
      try {
        const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);

        if (response.data && !response.data.erro) {
          setValue('address.street', response.data.logradouro);
          setValue('address.neighborhood', response.data.bairro);
          setValue('address.city', response.data.localidade);
          setValue('address.state', response.data.uf);

          // Feedback visual de sucesso (não altera a lógica de preenchimento)
          setCepFound(true);
          setCepFlash(true);
          setTimeout(() => setCepFlash(false), 400);
          setTimeout(() => setCepFound(false), 2000);
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      } finally {
        setIsLoadingCep(false);
      }
    }
  };

  // Função para buscar endereço pelo CEP (fallback no blur)
  const handleCepBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    await fetchCep(e.target.value);
  };

  // Função para formatar CEP
  const formatCep = (value: string) => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length <= 8) {
      return cleanValue.replace(/(\d{5})(\d{1,3})/, '$1-$2');
    }
    return cleanValue.slice(0, 8).replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  // Validação do formulário
  const validateForm = () => {
    return addressData.zipCode &&
           addressData.street &&
           addressData.number &&
           addressData.neighborhood &&
           addressData.city &&
           addressData.state &&
           watchedValues.acceptTerms;
  };

  const isFormValid = validateForm();

  // Estados derivados para checks de sucesso
  const cepValid = /^\d{5}-?\d{3}$/.test(addressData.zipCode || '');
  const stateValid = /^[A-Za-z]{2}$/.test(addressData.state || '');
  const flashClass = cepFlash ? ' flash' : '';

  return (
    <>
      <ZeplinTitle>Faça seu cadastro!</ZeplinTitle>
      <ZeplinSubtitle>Endereço</ZeplinSubtitle>

      <ZeplinStepIndicator>
        <span className="eyebrow">Passo 2 de 2</span>
        <div className="rail">
          <div className="dot completed"><CheckIcon /></div>
          <div className="line done"><div className="fill" /></div>
          <div className="dot current">2</div>
        </div>
        <div className="labels">
          <span>Dados</span>
          <span className="on">Endereço</span>
        </div>
      </ZeplinStepIndicator>

      {/* CEP — 1º campo, com feedback de loading/sucesso */}
      <ZeplinFieldGroup>
        <ZeplinLabel>CEP</ZeplinLabel>
        <ZeplinInput
          type="text"
          placeholder="00000-000"
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={9}
          className={`${(errors as any).address?.zipCode ? 'is-error' : cepValid ? 'is-valid' : ''}${flashClass}`}
          {...register('address.zipCode', {
            required: 'CEP é obrigatório',
            pattern: {
              value: /^\d{5}-?\d{3}$/,
              message: 'CEP deve ter 8 dígitos'
            }
          })}
          onChange={(e) => {
            const formatted = formatCep(e.target.value);
            e.target.value = formatted;
            setValue('address.zipCode', formatted);
            if (formatted.replace(/\D/g, '').length === 8) {
              fetchCep(formatted);
            }
          }}
          onBlur={handleCepBlur}
        />
        {isLoadingCep && (
          <LoadingSpinner
            style={{
              width: '16px',
              height: '16px',
              position: 'absolute',
              right: '14px',
              top: '38px',
              borderColor: 'rgba(77,20,140,.25)',
              borderTopColor: 'var(--brand-600)'
            }}
          />
        )}
        {!isLoadingCep && cepValid && !(errors as any).address?.zipCode && (
          <ZeplinValidCheck><CheckIcon /></ZeplinValidCheck>
        )}
        {(errors as any).address?.zipCode && (
          <ZeplinError role="alert">
            <AlertIcon /> {(errors as any).address.zipCode.message as string}
          </ZeplinError>
        )}
        {cepFound && (
          <ZeplinCepChip>
            <CheckIcon /> Endereço encontrado
          </ZeplinCepChip>
        )}
      </ZeplinFieldGroup>

      {/* Logradouro */}
      <ZeplinFieldGroup>
        <ZeplinLabel>Logradouro</ZeplinLabel>
        <ZeplinInput
          type="text"
          placeholder="Rua, avenida, etc."
          autoComplete="street-address"
          className={`${(errors as any).address?.street ? 'is-error' : ''}${flashClass}`}
          {...register('address.street', {
            required: 'Logradouro é obrigatório'
          })}
        />
        {(errors as any).address?.street && (
          <ZeplinError role="alert">
            <AlertIcon /> {(errors as any).address.street.message as string}
          </ZeplinError>
        )}
      </ZeplinFieldGroup>

      {/* Número + Complemento lado a lado */}
      <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 'var(--s-3)' }}>
        <ZeplinFieldGroup>
          <ZeplinLabel>Número</ZeplinLabel>
          <ZeplinInput
            type="text"
            placeholder="123"
            inputMode="numeric"
            className={(errors as any).address?.number ? 'is-error' : ''}
            {...register('address.number', {
              required: 'Número é obrigatório'
            })}
          />
          {(errors as any).address?.number && (
            <ZeplinError role="alert">
              <AlertIcon /> {(errors as any).address.number.message as string}
            </ZeplinError>
          )}
        </ZeplinFieldGroup>

        <ZeplinFieldGroup>
          <ZeplinLabel>
            Complemento <span className="optional">(opcional)</span>
          </ZeplinLabel>
          <ZeplinInput
            type="text"
            placeholder="Apartamento, bloco, etc."
            {...register('address.complement')}
          />
        </ZeplinFieldGroup>
      </div>

      {/* Bairro */}
      <ZeplinFieldGroup>
        <ZeplinLabel>Bairro</ZeplinLabel>
        <ZeplinInput
          type="text"
          placeholder="Digite o bairro"
          className={`${(errors as any).address?.neighborhood ? 'is-error' : ''}${flashClass}`}
          {...register('address.neighborhood', {
            required: 'Bairro é obrigatório'
          })}
        />
        {(errors as any).address?.neighborhood && (
          <ZeplinError role="alert">
            <AlertIcon /> {(errors as any).address.neighborhood.message as string}
          </ZeplinError>
        )}
      </ZeplinFieldGroup>

      {/* Cidade + UF lado a lado */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 'var(--s-3)' }}>
        <ZeplinFieldGroup>
          <ZeplinLabel>Cidade</ZeplinLabel>
          <ZeplinInput
            type="text"
            placeholder="Digite a cidade"
            autoComplete="address-level2"
            className={`${(errors as any).address?.city ? 'is-error' : ''}${flashClass}`}
            {...register('address.city', {
              required: 'Cidade é obrigatória'
            })}
          />
          {(errors as any).address?.city && (
            <ZeplinError role="alert">
              <AlertIcon /> {(errors as any).address.city.message as string}
            </ZeplinError>
          )}
        </ZeplinFieldGroup>

        <ZeplinFieldGroup>
          <ZeplinLabel>UF</ZeplinLabel>
          <ZeplinInput
            type="text"
            placeholder="SP"
            autoComplete="address-level1"
            maxLength={2}
            className={`${(errors as any).address?.state ? 'is-error' : stateValid ? 'is-valid' : ''}${flashClass}`}
            {...register('address.state', {
              required: 'Estado é obrigatório',
              maxLength: {
                value: 2,
                message: 'Estado deve ter 2 caracteres'
              }
            })}
            onChange={(e) => {
              e.target.value = e.target.value.toUpperCase();
            }}
          />
          {stateValid && !(errors as any).address?.state && (
            <ZeplinValidCheck><CheckIcon /></ZeplinValidCheck>
          )}
          {(errors as any).address?.state && (
            <ZeplinError role="alert">
              <AlertIcon /> {(errors as any).address.state.message as string}
            </ZeplinError>
          )}
        </ZeplinFieldGroup>
      </div>

      {/* Termos */}
      <ZeplinFieldGroup>
        <CheckboxContainer>
          <input
            type="checkbox"
            id="acceptTerms"
            {...register('acceptTerms', {
              required: 'Você deve aceitar os termos'
            })}
          />
          <ZeplinLabel htmlFor="acceptTerms" style={{ margin: 0, cursor: 'pointer' }}>
            Aceito os termos de uso e política de privacidade
          </ZeplinLabel>
        </CheckboxContainer>
        {(errors as any).acceptTerms && (
          <ZeplinError role="alert">
            <AlertIcon /> {(errors as any).acceptTerms.message as string}
          </ZeplinError>
        )}
      </ZeplinFieldGroup>

      {/* Honeypot anti-bot: escondido de humanos, mas preenchível por bots.
          Fora da tela e sem foco/autocomplete. Se vier preenchido, o backend
          descarta a requisição silenciosamente. */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        {...register('website')}
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          opacity: 0,
          pointerEvents: 'none'
        }}
      />

      <div style={{ display: 'flex', gap: 'var(--s-4)' }}>
        <ZeplinButton
          type="button"
          onClick={onBack}
          className="secondary"
          style={{ flex: 1 }}
        >
          Voltar
        </ZeplinButton>

        <ZeplinButton
          type="button"
          onClick={onSubmit}
          disabled={!isFormValid || isLoading}
          className={isFormValid && !isLoading ? 'enabled' : ''}
          style={{ flex: 2 }}
        >
          {isLoading ? (
            <>
              <LoadingSpinner style={{ width: '16px', height: '16px' }} />
              Criando cadastro...
            </>
          ) : (
            'Finalizar Cadastro'
          )}
        </ZeplinButton>
      </div>
    </>
  );
};

export default EnderecoStep;
